import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Bell,
  Sparkles,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  CircleDashed,
  ExternalLink,
  Cake,
  Inbox,
  Lightbulb,
} from "lucide-react";
import { api, type AttentionItem, type AttentionItemKind } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

interface Filter {
  id: string;
  label: string;
  kinds: AttentionItemKind[] | null;
  Icon: typeof Bell;
}

const FILTERS: Filter[] = [
  { id: "all", label: "All", kinds: null, Icon: Sparkles },
  { id: "action", label: "Action", kinds: ["engagement", "commitment", "follow_up"], Icon: CheckCircle2 },
  { id: "recommendation", label: "Suggested", kinds: ["recommendation"], Icon: Lightbulb },
  { id: "expiring", label: "Expiring", kinds: ["expiring"], Icon: Clock },
  { id: "birthday", label: "Birthdays", kinds: ["birthday"], Icon: Cake },
  { id: "brief", label: "From your brief", kinds: ["brief_item"], Icon: Inbox },
];

function bandFor(item: AttentionItem): "critical" | "thisWeek" | "later" {
  if (item.priorityScore >= 0.8) return "critical";
  if (item.priorityScore >= 0.5) return "thisWeek";
  return "later";
}

function isInferred(item: AttentionItem): boolean {
  return item.kind === "recommendation" || item.kind === "follow_up" || item.kind === "brief_item";
}

function kindBadge(kind: AttentionItemKind): { label: string; classes: string } {
  switch (kind) {
    case "engagement":
      return { label: "Engagement", classes: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300" };
    case "recommendation":
      return { label: "Suggested", classes: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300" };
    case "brief_item":
      return { label: "Brief", classes: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300" };
    case "birthday":
      return { label: "Birthday", classes: "bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300" };
    case "commitment":
      return { label: "Commitment", classes: "bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300" };
    case "expiring":
      return { label: "Expiring", classes: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" };
    case "follow_up":
      return { label: "Follow-up", classes: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300" };
  }
}

export function Attention() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterId = searchParams.get("filter") || "all";
  const activeFilter = FILTERS.find((f) => f.id === filterId) ?? FILTERS[0];

  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .attentionList({ kinds: activeFilter.kinds || undefined, limit: 200 })
      .then((r) => setItems(r.items || []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [activeFilter.kinds]);

  useEffect(refresh, [refresh]);
  useEffect(() => {
    const id = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const onActedOn = useCallback(async (item: AttentionItem) => {
    setBusyId(item.id);
    try {
      await api.attentionActedOn(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }, []);

  const onDismiss = useCallback(async (item: AttentionItem) => {
    setBusyId(item.id);
    try {
      await api.attentionDismiss(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }, []);

  const grouped = useMemo(() => {
    const out: Record<"critical" | "thisWeek" | "later", AttentionItem[]> = {
      critical: [],
      thisWeek: [],
      later: [],
    };
    for (const it of items) out[bandFor(it)].push(it);
    return out;
  }, [items]);

  const topPick = items.length > 0 ? items[0] : null;

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Attention" }]} />

      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            Attention
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            One stream of what Memso thinks deserves your time right now. Items
            auto-close when a thread goes quiet or a deadline passes.
          </p>
        </div>
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {loading ? "loading..." : `${items.length} item${items.length === 1 ? "" : "s"}`}
        </span>
      </header>

      <TeachingBanner
        id="attention-intro"
        title="What is this?"
        body="Attention is the one consolidated queue. It replaces the
              separate Engagements, Recommendations, Open Threads,
              Awaiting, Reminders, and Inferences pages. Filter chips
              switch views. Use Accept when you act on something, or
              Dismiss to teach Memso it was not useful."
      />

      <nav aria-label="Attention filters" className="-mx-1 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => {
          const Icon = f.Icon;
          const active = f.id === filterId;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (f.id === "all") next.delete("filter");
                else next.set("filter", f.id);
                setSearchParams(next, { replace: true });
              }}
              className={
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
                (active
                  ? "bg-sky-500 text-white shadow ring-2 ring-sky-300/40"
                  : "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700/60")
              }
              aria-pressed={active}
            >
              <Icon className="w-4 h-4" />
              {f.label}
            </button>
          );
        })}
      </nav>

      {error && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && (
        <EmptyState />
      )}

      {topPick && (
        <section aria-labelledby="hero-attention" className="space-y-2">
          <h2 id="hero-attention" className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Top of mind
          </h2>
          <HeroCard item={topPick} onActedOn={onActedOn} onDismiss={onDismiss} busy={busyId === topPick.id} />
        </section>
      )}

      {grouped.critical.length > 0 && (
        <Group label="Critical" tone="critical" items={grouped.critical} onActedOn={onActedOn} onDismiss={onDismiss} busyId={busyId} skipFirstWhenHero={!!topPick} />
      )}
      {grouped.thisWeek.length > 0 && (
        <Group label="This week" tone="this-week" items={grouped.thisWeek} onActedOn={onActedOn} onDismiss={onDismiss} busyId={busyId} skipFirstWhenHero={!topPick ? false : grouped.critical.length === 0} />
      )}
      {grouped.later.length > 0 && (
        <Group label="Whenever" tone="later" items={grouped.later} onActedOn={onActedOn} onDismiss={onDismiss} busyId={busyId} skipFirstWhenHero={false} />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-6 text-center space-y-2">
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
      </div>
      <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
        Nothing needs you right now.
      </p>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
        Items land here when a thread goes quiet, a deadline approaches, or
        Memso drafts something for you. The signaler ticks every 5 minutes.
      </p>
    </div>
  );
}

function HeroCard({
  item,
  onActedOn,
  onDismiss,
  busy,
}: {
  item: AttentionItem;
  onActedOn: (i: AttentionItem) => void;
  onDismiss: (i: AttentionItem) => void;
  busy: boolean;
}) {
  const inferred = isInferred(item);
  const badge = kindBadge(item.kind);
  return (
    <article
      className={
        "rounded-2xl p-5 space-y-3 bg-gradient-to-br from-sky-50/80 to-white dark:from-sky-900/30 dark:to-slate-900/40 " +
        (inferred
          ? "border-2 border-dashed border-amber-400/60 dark:border-amber-500/40"
          : "border border-sky-200/70 dark:border-sky-700/40")
      }
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${badge.classes}`}>
              {badge.label}
            </span>
            {inferred && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                <CircleDashed className="w-3 h-3" />
                Inferred
              </span>
            )}
            {item.priorityScore >= 0.8 && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                <AlertTriangle className="w-3 h-3" />
                Critical
              </span>
            )}
          </div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {item.title || item.summary || "Untitled"}
          </h3>
          {item.summary && item.title && item.summary !== item.title && (
            <p className="text-sm text-slate-700 dark:text-slate-200">{item.summary}</p>
          )}
          {item.reason && (
            <p className="text-xs italic text-slate-500 dark:text-slate-400">{item.reason}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <button
          type="button"
          onClick={() => onActedOn(item)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white px-3.5 py-2 text-sm font-semibold transition disabled:opacity-50"
        >
          <CheckCircle2 className="w-4 h-4" />
          {inferred ? "Confirm" : "Accept"}
        </button>
        <button
          type="button"
          onClick={() => onDismiss(item)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/70 dark:bg-slate-800/70 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3.5 py-2 text-sm font-semibold border border-slate-300 dark:border-slate-600 transition disabled:opacity-50"
        >
          <XCircle className="w-4 h-4" />
          Dismiss
        </button>
        <DrillInLink item={item} />
      </div>
    </article>
  );
}

function Group({
  label,
  tone,
  items,
  onActedOn,
  onDismiss,
  busyId,
  skipFirstWhenHero,
}: {
  label: string;
  tone: "critical" | "this-week" | "later";
  items: AttentionItem[];
  onActedOn: (i: AttentionItem) => void;
  onDismiss: (i: AttentionItem) => void;
  busyId: string | null;
  skipFirstWhenHero: boolean;
}) {
  const display = skipFirstWhenHero ? items.slice(1) : items;
  if (display.length === 0) return null;
  const toneClasses =
    tone === "critical"
      ? "text-rose-600 dark:text-rose-400"
      : tone === "this-week"
        ? "text-amber-600 dark:text-amber-400"
        : "text-slate-500 dark:text-slate-400";
  return (
    <section className="space-y-2">
      <h2 className={`text-xs font-semibold uppercase tracking-wider ${toneClasses}`}>
        {label} <span className="text-slate-400 dark:text-slate-500">({display.length})</span>
      </h2>
      <ul className="space-y-2">
        {display.map((it) => (
          <li key={it.id}>
            <Row item={it} onActedOn={onActedOn} onDismiss={onDismiss} busy={busyId === it.id} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function Row({
  item,
  onActedOn,
  onDismiss,
  busy,
}: {
  item: AttentionItem;
  onActedOn: (i: AttentionItem) => void;
  onDismiss: (i: AttentionItem) => void;
  busy: boolean;
}) {
  const inferred = isInferred(item);
  const badge = kindBadge(item.kind);
  return (
    <article
      className={
        "rounded-xl bg-white/70 dark:bg-slate-900/50 px-4 py-3 flex items-start justify-between gap-3 flex-wrap " +
        (inferred
          ? "border-2 border-dashed border-amber-400/50 dark:border-amber-500/30"
          : "border border-slate-200/70 dark:border-slate-700/40")
      }
    >
      <div className="space-y-1 min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.classes}`}>
            {badge.label}
          </span>
          {inferred && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              Inferred
            </span>
          )}
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {item.title || item.summary || "Untitled"}
          </h3>
        </div>
        {item.summary && item.title && item.summary !== item.title && (
          <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{item.summary}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={() => onActedOn(item)}
          disabled={busy}
          aria-label={inferred ? "Confirm" : "Accept"}
          title={inferred ? "Confirm" : "Accept"}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition disabled:opacity-50"
        >
          <CheckCircle2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onDismiss(item)}
          disabled={busy}
          aria-label="Dismiss"
          title="Dismiss"
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/80 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 transition disabled:opacity-50"
        >
          <XCircle className="w-4 h-4" />
        </button>
        <DrillInLink item={item} compact />
      </div>
    </article>
  );
}

function DrillInLink({ item, compact = false }: { item: AttentionItem; compact?: boolean }) {
  let href: string | null = null;
  if (item.subjectEntityKind === "person" && item.subjectEntityId) {
    href = `/people/${encodeURIComponent(item.subjectEntityId)}`;
  } else if (item.kind === "engagement") {
    href = `/engagements/${encodeURIComponent(item.sourceRef)}`;
  }
  if (!href) return null;
  if (compact) {
    return (
      <Link
        to={href}
        aria-label="Open"
        title="Open"
        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/80 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 transition"
      >
        <ExternalLink className="w-4 h-4" />
      </Link>
    );
  }
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1.5 rounded-lg bg-white/80 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3.5 py-2 text-sm font-semibold border border-slate-300 dark:border-slate-600 transition"
    >
      <ExternalLink className="w-4 h-4" />
      Open
    </Link>
  );
}
